import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../context/AuthContext';
import { getUserSchema } from '../../../Utils/validate';

import useUser from '../../../hooks/useUser';
import { useForm, Resolver } from 'react-hook-form';
import FormUI from './FormUI';
import CompleteProfileDfcVillage, {
  canUseDfcVillageSignupForm,
} from './variants/dfcVillageTheme';
import { ICompleteProfile, IFormTypes } from './Types';
import { CheckoutContext, CheckoutContextProps } from '../../../context/CheckoutContext';
import dayjs from 'dayjs';
import { uploadProfileImageToStorage } from '../../../Utils/uploadProfileImage';
import { toast } from 'react-toastify';
import { ADD_NEW_SCHOOL_OPTION_ID } from './SchoolSelectField';
import { addCompanySchool } from '../../../services/schoolApi';
import {
  rememberPostAuthReturn,
  rememberPostAuthReturnIfAbsent,
} from '../../../Utils/postAuthReturn';
import { localizeMessage } from '../../../Utils/dfcMessages';

const CompleteProfile: React.FC<ICompleteProfile> = ({
  isSocialLogin,
  email,
  redirectToLogin,
  redirectToCalendar = true,
  layout,
}) => {
  const { company, setCompany, selectedSession } = useContext(CheckoutContext) as CheckoutContextProps;
  const navigate = useNavigate();

  const navigateToMyProfile = () => {
    navigate(`/My-profile?companyId=${company?.id}`);
  };

  const finishEmbeddedOrStandaloneSignup = (
    userEmail?: string,
    options: { requireEmailVerification?: boolean } = {}
  ) => {
    if (options.requireEmailVerification && company?.hasEmailVerificationRequired) {
      if (redirectToCalendar) {
        // Standalone /Register (or direct visit) falls back to the homepage
        // unless an origin (e.g. the calendar) was stored earlier.
        rememberPostAuthReturnIfAbsent(selectedSession, `/?companyId=${company?.id}`);
      } else {
        rememberPostAuthReturn(selectedSession);
      }
      navigate(
        `/VerifyCode?companyId=${company?.id}&email=${encodeURIComponent(userEmail || '')}`
      );
      return;
    }
    if (redirectToCalendar) {
      navigateToMyProfile();
    }
  };
  const { user, setUser, signUp } = useAuth();
  const { updateUser, sendWelcomeEmail } = useUser();

  const [hasChildren, setHasChildren] = useState(false);
  const [hasPods, setHasPods] = useState(false);

  const resolver: Resolver<IFormTypes> = async (data, context, options) => {
    try {
      const schema = getUserSchema(!isSocialLogin, company!, hasChildren, hasPods);
      const values = await schema.validate(data, { abortEarly: false });
      return { values, errors: {} };
    } catch (validationError: any) {
      return {
        values: {},
        errors: validationError.inner.reduce((allErrors: any, currentError: any) => {
          return {
            ...allErrors,
            [currentError.path]: {
              type: currentError.type ?? 'validation',
              message: currentError.message,
            },
          };
        }, {}),
      };
    }
  };

  const completeProfileForm = useForm<IFormTypes>({
    resolver,
    defaultValues: { isBusinessAccount: false, referredByCode: '' },
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (email) completeProfileForm.setValue('email', email);
  }, [email, completeProfileForm]);

  const onSubmit = async (data: IFormTypes) => {
    setIsLoading(true);

    // Handle profile image upload if provided
    let profileImageUrl: string | undefined;
    if (data.profileImage) {
      try {
        const userEmail = user?.email || data.email; // Use user email or form email
        profileImageUrl = await uploadProfileImageToStorage(data.profileImage, userEmail);
      } catch (error: any) {
        console.error('Profile image upload failed:', error);
        toast.error(localizeMessage('Profile image upload failed. Continuing with registration...'));
        // Continue with registration even if image upload fails
      }
    }
    delete data.profileImage;

    try {
      let schoolId = data.schoolId;
      let schoolName = data.schoolName;

      if (schoolId === ADD_NEW_SCHOOL_OPTION_ID) {
        if (!company?.id || !data.newSchoolName?.trim()) {
          throw new Error('School name is required');
        }
        const result = await addCompanySchool(company.id, data.newSchoolName.trim());
        schoolId = result.school.id;
        schoolName = result.school.name;
        if (setCompany && company) {
          setCompany({ ...company, schools: result.schools });
        }
      }

      const { newSchoolName: _newSchoolName, ...profileData } = data;
      const payload = {
        ...profileData,
        schoolId,
        schoolName,
        isProfileCompleted: true,
        favoriteSports: data.favoriteSports?.filter(val => val !== undefined),
        dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth).format('YYYY-MM-DD') : undefined,
        profileImageUrl,
      };

      if (isSocialLogin) {
        if (!user) throw new Error('You should Login first');

        const response = await updateUser({
          ...user,
          ...payload,
        });
        setUser(response);
        await sendWelcomeEmail(user.id!);
        finishEmbeddedOrStandaloneSignup(response.email || data.email);
      } else {
        const response = await signUp(
          {
            ...payload,
            isEmailVerified: company?.hasEmailVerificationRequired ? false : true,
            tenantId: '',
          },
          company
        );
        setUser(response);

        if (response?.error) {
          completeProfileForm.setError('privacy', {
            message: response.error.message,
          });
        } else {
          finishEmbeddedOrStandaloneSignup(data.email, { requireEmailVerification: true });
        }
      }
    } catch (error: any) {
      completeProfileForm.setError('privacy', {
        message: error.message || 'Failed to complete profile',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // The village form draws a subset of the sign-up fields; a company that asks for more keeps
  // the generic form so no required field is ever hidden.
  if (company?.theme === 'dfcVillageTheme' && canUseDfcVillageSignupForm(company)) {
    return (
      <CompleteProfileDfcVillage
        form={completeProfileForm}
        isSignupWithEmailAndPassword={!isSocialLogin}
        onSubmit={onSubmit}
        isLoading={isLoading}
        hasChildren={hasChildren}
        setHasChildren={setHasChildren}
        hasPods={hasPods}
        setHasPods={setHasPods}
        company={company}
        layout={layout}
      />
    );
  }

  return (
    <FormUI
      form={completeProfileForm}
      isSignupWithEmailAndPassword={!isSocialLogin}
      onSubmit={onSubmit}
      isLoading={isLoading}
      hasChildren={hasChildren}
      setHasChildren={setHasChildren}
      hasPods={hasPods}
      setHasPods={setHasPods}
    />
  );
};

export default CompleteProfile;
